import {I18n} from 'aws-amplify/utils';
import {
    Alert,
    Button,
    Code,
    Drawer,
    DrawerProps,
    Group,
    NumberInput,
    ScrollArea,
    Select,
    Stack,
    Text,
} from '@mantine/core';
import {FlexiCopyButton} from '@flexinet/ui-components';

import {DnsZone} from '../types';
import {
    DEFAULT_DELEGATION_TTL_SECONDS,
    delegationRecordFor,
    undelegationRecordFor,
    parentWriteCandidates,
} from '../services/delegation';
import {delegationStatusOf} from '../types';
import {useZones} from '../hooks/useZones';
import {useZone} from '../hooks/useZone';
import {useZoneCreateConnections} from '../hooks/useZoneCreateConnections';
import {useDelegateZoneMutation} from '../hooks/useZoneDelegation';

// FNP-1466 — THE DELEGATION DRAWER.
//
// ⚠️ THE OPERATOR SUPPLIES ONE THING: which connection writes the parent.
// Everything else is derived from the row they selected — the record name is
// the child's name, the type is NS, and the values are the child's own
// nameservers. The manual path this replaces (FNP-1465) required copying four
// nameserver strings between two pages in two accounts, and every one of them
// was a chance to paste the wrong thing.
//
// ⚠️ THE RECORD IS SHOWN BEFORE SAVE. Same principle as the trust policy on the
// connection page (FNP-1469): show the document rather than asking the operator
// to trust that the right one is being assembled. A delegation is a destructive
// change to where a whole subtree of DNS resolves; seeing it first is cheap.
//
// ⚠️ NO CONNECTION IS NOT AN ERROR. A customer whose parent domain sits at an
// external registrar has no connection that can write it, and that is the
// MAJORITY case rather than an edge. For them the record on screen IS the
// feature: they copy it and add it wherever their parent lives.

/** The form state, owned by the caller so a remount cannot discard it. */
export type DelegationFormState = {
    ttlSeconds: number,
    setTtlSeconds: (value: number) => void,
    parentConnectionId: string | null,
    setParentConnectionId: (value: string | null) => void,
};

export const DelegateZoneDrawer = (
    {
        zone,
        form,
        opened,
        onClose,
        ...rest
    }: DrawerProps & {
        zone?: DnsZone,
        form: DelegationFormState,
    },
) => {

    // ⚠️ THE FORM STATE IS OWNED BY THE CALLER, NOT BY THIS DRAWER, AND THAT IS
    // NOT A STYLE CHOICE.
    //
    // This drawer is mounted from the detail page's `tools` array, and the page
    // rebuilds that array whenever its `useZone` query settles — which REMOUNTS
    // the drawer. A `useState` here is wiped by that, and so is a `useRef`: a
    // remount gives the component a brand new ref, not the previous one. I
    // tried the ref first and it failed for exactly that reason.
    //
    // Observed before the fix: the connection picker silently emptied between
    // choosing a connection and pressing the button, so the click did nothing,
    // no request was made and no error appeared anywhere. A form that discards
    // input with no signal is worse than one that refuses it loudly.
    //
    // `ManageDelegationButton` does not remount, so the state lives there.
    const {
        ttlSeconds,
        setTtlSeconds,
        parentConnectionId,
        setParentConnectionId,
    } = form;

    const {rawItems} = useZones();
    const {allConnections} = useZoneCreateConnections();
    const delegateMutation = useDelegateZoneMutation();

    // ⚠️ THE NAMESERVERS MUST COME FROM A SINGLE-ZONE READ, NEVER FROM A LIST
    // ROW. The Route 53 adapter says so at `listZones`: "ListHostedZones does
    // not return the delegation set, and fetching it per zone would turn one
    // call into N. Zones list with no nameservers; getZone is where they
    // arrive." A list row therefore has `nameservers: []` — the "—" in the
    // list's Nameservers column — and a record built from it would carry an
    // empty NS set.
    //
    // Today's only caller is the detail page, which has already read the whole
    // zone, so this resolves from the same React Query cache entry rather than
    // issuing a second request. It is here so the drawer is correct on its own
    // terms: whatever zone it is handed, the values it writes come from a
    // getZone read. That also satisfies the ticket's freshness requirement — a
    // zone deleted and recreated gets a DIFFERENT delegation set, and a stale
    // create response would delegate to nameservers that no longer serve it.
    const {record: freshZone} = useZone(zone?.connectionId, zone?.id);

    if (!zone) {
        return null;
    }

    // Prefer the freshly read zone; fall back to the row while it loads, which
    // renders the no-nameservers notice rather than a wrong record.
    const subject = freshZone ?? zone;
    const record = delegationRecordFor(subject, ttlSeconds);
    // `subject` rather than `zone` so the parent name comes from the same read
    // the record did — the two must never describe different zones.
    const candidates = parentWriteCandidates(subject, rawItems, allConnections);

    // The parent zone row reached through the chosen connection — the zone the
    // record is written INTO. Resolved from rows already loaded.
    const parentZone = parentConnectionId
        ? rawItems.find(
            row => row.name === zone.delegationParentName
                && row.connectionId === parentConnectionId,
        )
        : undefined;

    // ⚠️ WHAT THE PARENT SAYS TODAY, WHICH DECIDES WHICH ACTION IS OFFERED.
    // DELEGATED means an NS record for this child already exists in the parent
    // and removal is the meaningful action. Every other status — including
    // PARTIAL and NOT_DETERMINED — leaves adding available, because writing the
    // correct record is the fix for a half-applied or unknown delegation.
    const currentlyDelegated = delegationStatusOf(subject) === 'DELEGATED';
    const removal = undelegationRecordFor(subject, ttlSeconds);

    const submit = async (change: typeof record) => {
        if (!change || !parentConnectionId || !parentZone) {
            return;
        }
        // ⚠️ AWAITED, AND A FAILURE PROPAGATES — the AddFormDrawer rule. A
        // rejected write must leave the drawer open with the operator's choice
        // intact rather than closing over a failure they can no longer see.
        await delegateMutation.mutateAsync({
            parentConnectionId,
            parentZoneId: parentZone.id,
            record: change,
            childZoneName: zone.name,
            parentZoneName: subject.delegationParentName ?? undefined,
        });
        onClose();
    };

    const handleSubmit = () => submit(record);
    const handleRemove = () => submit(removal);

    const recordText = record
        ? `${record.name}  ${record.ttlSeconds}  IN  NS\n`
            + record.values.map(v => `    ${v}`).join('\n')
        : '';

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            position={'right'}
            size={'xl'}
            scrollAreaComponent={ScrollArea.Autosize}
            title={I18n.get('Manage delegation')}
            transitionProps={{transition: 'slide-up', duration: 750}}
            {...rest}
        >
            <Stack gap={'md'}>

                {/* The apex case says its piece in the alert below, so the
                    header carries only the parent when there is one rather
                    than spending a second line saying there is not. */}
                <Group gap={'xs'}>
                    <Code>{zone.name}</Code>
                    {zone.delegationParentName && (
                        <Text size={'sm'} c={'dimmed'}>
                            {I18n.get('delegated from {parent}')
                                .split('{parent}').join(zone.delegationParentName)}
                        </Text>
                    )}
                </Group>

                {/* ⚠️ AN APEX HAS NOTHING TO DELEGATE FROM. Its nameservers are
                    set at the registrar, and there is no parent zone anywhere
                    to write into — so this is a statement of fact, not a
                    failure the operator can fix here. */}
                {!zone.delegationParentName && (
                    <Alert color={'gray'} variant={'light'} data-testid={'delegate-no-parent'}>
                        <Text size={'sm'}>
                            {I18n.get('This zone is a domain apex. Its nameservers live at the registrar.')}
                        </Text>
                    </Alert>
                )}

                {/* ⚠️ NO NAMESERVERS MEANS NO HONEST RECORD. Writing an empty
                    NS set would assert that the zone is served by nothing,
                    which a resolver will believe. */}
                {zone.delegationParentName && !record && (
                    <Alert color={'yellow'} variant={'light'} data-testid={'delegate-no-nameservers'}>
                        <Text size={'sm'}>
                            {I18n.get('This zone publishes no nameservers yet. Reload.')}
                        </Text>
                    </Alert>
                )}

                {record && (
                    <>
                        <Stack gap={'xs'}>
                            <Group justify={'space-between'}>
                                {/* ⚠️ NAMED FOR THE RECORD, NOT FOR AN ACTION.
                                    Add and Remove can both be available at
                                    once, and this same record is the subject
                                    of either — "the record to add" would be a
                                    lie on the button the operator is about to
                                    press half the time. */}
                                <Text size={'sm'} fw={500}>
                                    {I18n.get('The delegation record')}
                                </Text>
                                <FlexiCopyButton
                                    value={recordText}
                                    data-testid={'delegation-record-copy'}
                                />
                            </Group>
                            {/* Shown for BOTH paths: the operator who is about
                                to Save sees what will be written, and the one
                                whose parent is elsewhere copies it. */}
                            <Code block data-testid={'delegation-record'}>
                                {recordText}
                            </Code>
                        </Stack>

                        <NumberInput
                            label={I18n.get('Time to live (seconds)')}
                            // 172800 (48h) is the convention every existing
                            // delegation in dev.flexi.network uses; the default
                            // already carries it, so the field does not need to
                            // explain caching to justify the number in it.
                            description={I18n.get('Resolver cache lifetime.')}
                            value={ttlSeconds}
                            onChange={value => setTtlSeconds(
                                typeof value === 'number' ? value : DEFAULT_DELEGATION_TTL_SECONDS,
                            )}
                            min={60}
                            data-testid={'delegation-ttl'}
                        />

                        {candidates.length > 0
                            ? (
                                <Select
                                    label={I18n.get('Connection that writes the parent')}
                                    // ⚠️ THE NARROWING IS STATED, not left for
                                    // the operator to infer from a short list.
                                    // A picker that silently omits connections
                                    // reads as a bug; one that says what it
                                    // filtered on reads as a decision.
                                    description={I18n.get('Only connections that can write {parent}.')
                                        .split('{parent}').join(zone.delegationParentName ?? '')}
                                    placeholder={I18n.get('Choose a connection')}
                                    data={candidates.map(c => ({
                                        value: c.id,
                                        label: c.displayName,
                                    }))}
                                    value={parentConnectionId}
                                    onChange={setParentConnectionId}
                                    data-testid={'delegation-connection'}
                                />
                            )
                            : (
                                // ⚠️ THE REGISTRAR CASE, AND IT IS NOT AN ERROR.
                                // Most customers' parent domains are not in an
                                // account they connected. Saying so plainly,
                                // with the record above ready to copy, is the
                                // whole feature for them.
                                <Alert color={'blue'} variant={'light'} data-testid={'delegate-no-connection'}>
                                    <Text size={'sm'}>
                                        {I18n.get('No connection can write {parent}. Add the record above wherever it is managed.')
                                            .split('{parent}').join(zone.delegationParentName ?? '')}
                                    </Text>
                                </Alert>
                            )}
                    </>
                )}

                {/* ⚠️ REMOVING A DELEGATION TAKES A LIVE SUBTREE OFFLINE, and
                    says so before it is offered. Every hostname under the child
                    zone stops resolving the moment the parent forgets it — the
                    zone itself survives untouched, which is the distinction an
                    operator needs to weigh. */}
                {currentlyDelegated && candidates.length > 0 && (
                    <Alert color={'yellow'} variant={'light'} data-testid={'delegate-remove-warning'}>
                        <Text size={'sm'}>
                            {I18n.get('Removing this takes every hostname under {child} offline. The zone itself is not deleted.')
                                .split('{child}').join(subject.name)}
                        </Text>
                    </Alert>
                )}

                <Group justify={'flex-end'}>
                    <Button variant={'default'} onClick={onClose}>
                        {candidates.length > 0
                            ? I18n.get('Cancel')
                            : I18n.get('Close')}
                    </Button>
                    {/* ⚠️ OFFERED ONLY WHEN THE PARENT ACTUALLY DELEGATES.
                        A DELETE against a record that is not there is refused
                        by the provider, so showing Remove on an undelegated
                        zone would be offering a button that cannot work. */}
                    {currentlyDelegated && candidates.length > 0 && (
                        <Button
                            color={'red'}
                            variant={'outline'}
                            onClick={handleRemove}
                            loading={delegateMutation.isPending}
                            disabled={!removal || !parentConnectionId}
                            data-testid={'delegation-remove'}
                        >
                            {I18n.get('Remove delegation')}
                        </Button>
                    )}
                    {candidates.length > 0 && (
                        <Button
                            onClick={handleSubmit}
                            loading={delegateMutation.isPending}
                            disabled={!record || !parentConnectionId}
                            data-testid={'delegation-save'}
                        >
                            {I18n.get('Save')}
                        </Button>
                    )}
                </Group>
            </Stack>
        </Drawer>
    );
};
